import React, { useState, useEffect, useCallback } from 'react';
import { Users, ChevronRight, X, MapPin, Coffee, AlertCircle, ShieldOff, ListChecks, ClipboardCheck, Plus, Navigation } from 'lucide-react';
import { safeFetch } from '../../lib/safeFetch';
import { startPoll } from '../../lib/poll';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  on_visit: { label: 'On Visit', cls: 'bg-orange-100 text-orange-700' },
  on_break: { label: 'On Break', cls: 'bg-amber-100 text-amber-700' },
  on_permission: { label: 'On Permission', cls: 'bg-violet-100 text-violet-700' },
  in_office: { label: 'In Office', cls: 'bg-emerald-100 text-emerald-700' },
  checked_out: { label: 'Checked Out', cls: 'bg-gray-100 text-gray-600' },
  not_checked_in: { label: 'Not Checked In', cls: 'bg-red-50 text-red-600' },
};

function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const diffSec = Math.max(0, (Date.now() - t) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  } catch { return '—'; }
}

export default function TeamTab() {
  const [team, setTeam] = useState([]);
  const [live, setLive] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const [tRes, lRes] = await Promise.all([
        fetch(`${API_URL}/api/team/my-team`, { headers }),
        fetch(`${API_URL}/api/team/live-status`, { headers }),
      ]);
      if (tRes.ok) setTeam(await tRes.json());
      if (lRes.ok) setLive(await lRes.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => startPoll(fetchAll, 45_000), [fetchAll]);  // v2.6.4: was 7 s

  if (loading) return <p className="p-4 text-center text-sm text-gray-500">Loading team…</p>;

  if (team.length === 0) {
    return (
      <div className="bg-white rounded-xl p-6 text-center shadow-sm" data-testid="team-empty">
        <Users size={32} className="mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">You don't have any team members yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="team-tab">
      <div className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-blue-600" />
          <h3 className="font-semibold text-sm">My Team ({team.length})</h3>
        </div>
        <span className="text-[10px] text-gray-400">refreshes every 7s</span>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <ul className="divide-y divide-gray-100">
          {team.map(m => {
            const ls = live[m.id] || {};
            const meta = STATUS_META[ls.status] || STATUS_META.not_checked_in;
            const loc = ls.current_location;
            return (
              <li key={m.id}>
                <button
                  onClick={() => setSelected(m)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                  data-testid={`team-member-${m.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-blue-700 text-sm">{(m.name || '?').charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium truncate">{m.name}</p>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${meta.cls}`}
                          data-testid={`team-status-${m.id}`}
                        >{meta.label}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">
                        <span className="capitalize">{m.role}</span>{m.staff_id ? ` · ${m.staff_id}` : ''}
                      </p>
                      {loc && loc.lat != null && (
                        <p className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1 truncate" data-testid={`team-location-${m.id}`}>
                          <Navigation size={10} className="text-blue-500 flex-shrink-0" />
                          <a
                            href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`}
                            target="_blank" rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:underline truncate"
                          >{loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}</a>
                          <span className="text-gray-400 flex-shrink-0">· {fmtRelative(loc.timestamp)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {selected && <MemberDrillDown member={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function MemberDrillDown({ member, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [tab, setTab] = useState('visits');
  const [data, setData] = useState({ visits: null, breaks: null, missing: null, permissions: null, todos: null, attendance: null });
  const [loading, setLoading] = useState(false);
  const [todoReload, setTodoReload] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const safe = (p) => p.then(r => r.ok ? r.json() : null).catch(() => null);
      const [v, b, m, p, t, att] = await Promise.all([
        safe(fetch(`${API_URL}/api/team/${member.id}/visits?date=${date}`, { headers })),
        safe(fetch(`${API_URL}/api/team/${member.id}/breaks?date=${date}`, { headers })),
        safe(fetch(`${API_URL}/api/team/${member.id}/missing-log?date=${date}`, { headers })),
        safe(fetch(`${API_URL}/api/team/${member.id}/permissions?date=${date}`, { headers })),
        safe(fetch(`${API_URL}/api/team/${member.id}/todos?status=pending`, { headers })),
        safe(fetch(`${API_URL}/api/team/${member.id}/attendance?date=${date}`, { headers })),
      ]);
      setData({
        visits: v || [],
        breaks: b || [],
        missing: m || { gps_gaps: [], geofence_exits: [] },
        permissions: p || [],
        todos: t || [],
        attendance: att || [],
      });
      setLoading(false);
    })();
  }, [member.id, date, todoReload]);

  const SUB_TABS = [
    { id: 'visits', label: 'Visits', icon: MapPin },
    { id: 'breaks', label: 'Breaks', icon: Coffee },
    { id: 'permissions', label: 'Permissions', icon: ShieldOff },
    { id: 'todos', label: 'Tasks', icon: ListChecks },
    { id: 'attendance', label: 'Attendance', icon: ClipboardCheck },
    { id: 'missing', label: 'Missing', icon: AlertCircle },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="team-drilldown">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-base">{member.name}</h3>
            <p className="text-[11px] text-gray-500 capitalize">{member.role}{member.staff_id ? ` · ${member.staff_id}` : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100" data-testid="team-drill-close">
            <X size={18} />
          </button>
        </div>
        {/* Date picker */}
        <div className="px-4 py-2 border-b flex items-center gap-2 text-sm">
          <span className="text-gray-500 text-xs">Date:</span>
          <input
            type="date" value={date} max={today}
            onChange={(e) => setDate(e.target.value)}
            className="flex-1 px-2 py-1 border rounded text-sm"
            data-testid="team-drill-date"
          />
        </div>
        {/* Sub-tabs (scrollable horizontal row) */}
        <div className="px-2 py-2 border-b overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {SUB_TABS.map(t => {
              const Ic = t.icon;
              const isOn = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold whitespace-nowrap ${isOn ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-700'}`}
                  data-testid={`team-drill-tab-${t.id}`}
                ><Ic size={12} />{t.label}</button>
              );
            })}
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 text-sm">
          {loading && <p className="text-center text-gray-500 py-6">Loading…</p>}
          {!loading && tab === 'visits' && <VisitsList rows={data.visits || []} />}
          {!loading && tab === 'breaks' && <BreaksList rows={data.breaks || []} />}
          {!loading && tab === 'permissions' && <PermissionsList rows={data.permissions || []} />}
          {!loading && tab === 'todos' && (
            <TodosList
              rows={data.todos || []}
              memberId={member.id}
              memberName={member.name}
              onAdded={() => setTodoReload(x => x + 1)}
            />
          )}
          {!loading && tab === 'attendance' && <AttendanceList rows={data.attendance || []} />}
          {!loading && tab === 'missing' && <MissingList ml={data.missing || { gps_gaps: [], geofence_exits: [] }} />}
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
}

function VisitsList({ rows }) {
  if (rows.length === 0) return <p className="text-center text-gray-500 py-6">No visits.</p>;
  return (
    <ul className="space-y-2">
      {rows.map(v => (
        <li key={v.id} className="border rounded-lg p-2" data-testid={`team-visit-${v.id}`}>
          <div className="flex items-center justify-between">
            <span className="font-medium truncate flex-1">{(v.supplier_names || []).join(', ') || 'Visit'}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${v.status === 'ongoing' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>{v.status}</span>
          </div>
          <p className="text-[11px] text-gray-500 truncate">{v.purpose || '—'}</p>
          <div className="text-[11px] text-gray-600 mt-1 flex flex-wrap gap-x-3">
            <span>{fmtTime(v.start?.time)} → {fmtTime(v.end?.time)}</span>
            <span>{v.distance_km ?? 0} km</span>
            <span>{v.duration_hours ?? 0} h</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function BreaksList({ rows }) {
  if (rows.length === 0) return <p className="text-center text-gray-500 py-6">No breaks.</p>;
  return (
    <ul className="space-y-2">
      {rows.map(b => (
        <li key={b.id} className="border rounded-lg p-2 flex items-center justify-between" data-testid={`team-break-${b.id}`}>
          <div>
            <p className="font-medium capitalize">{(b.type || '').replace('_', ' ')}</p>
            <p className="text-[11px] text-gray-500">{fmtTime(b.out?.time)} → {fmtTime(b.in?.time)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold">{b.duration_hours ? `${Math.round(b.duration_hours * 60)}m` : (b.status === 'ongoing' ? 'ongoing' : '—')}</p>
            {b.excess_duration_hours > 0 && <p className="text-[10px] text-red-600">+{Math.round(b.excess_duration_hours * 60)}m</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function MissingList({ ml }) {
  const { gps_gaps = [], geofence_exits = [] } = ml;
  if (gps_gaps.length === 0 && geofence_exits.length === 0) {
    return <p className="text-center text-emerald-700 py-6">All clear.</p>;
  }
  return (
    <div className="space-y-3">
      {geofence_exits.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-red-700 mb-1">Out of Office (unauthorised) · {geofence_exits.length}</p>
          <ul className="space-y-1">
            {geofence_exits.map((g, i) => (
              <li key={`gex-${g.from || ''}-${g.to || ''}-${i}`} className="border rounded-lg px-2 py-1.5 flex items-center justify-between text-xs">
                <span>{fmtTime(g.from)} → {fmtTime(g.to)}</span>
                <span className="font-semibold text-red-700">{g.duration_minutes}m</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {gps_gaps.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-orange-700 mb-1">GPS Gaps · {gps_gaps.length}</p>
          <ul className="space-y-1">
            {gps_gaps.map((g, i) => (
              <li key={`gap-${g.from || ''}-${g.to || ''}-${i}`} className="border rounded-lg px-2 py-1.5 flex items-center justify-between text-xs">
                <span>{fmtTime(g.from)} → {fmtTime(g.to)}</span>
                <span className="font-semibold text-orange-700">{g.duration_minutes}m</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PermissionsList({ rows }) {
  if (rows.length === 0) return <p className="text-center text-gray-500 py-6">No permissions for this date.</p>;
  return (
    <ul className="space-y-2">
      {rows.map(p => {
        const apr = p.approval || {};
        const tone = apr.status === 'approved' ? 'bg-emerald-100 text-emerald-700'
          : apr.status === 'rejected' ? 'bg-red-100 text-red-700'
          : p.status === 'ongoing' ? 'bg-orange-100 text-orange-700'
          : 'bg-gray-100 text-gray-700';
        return (
          <li key={p.id} className="border rounded-lg p-2" data-testid={`team-permission-${p.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium truncate flex-1">{p.reason || 'Permission'}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${tone}`}>
                {apr.status === 'pending' ? p.status : apr.status}
              </span>
            </div>
            <div className="text-[11px] text-gray-600 mt-1 flex flex-wrap gap-x-3">
              <span>Req: {p.requested_hours ?? 0}h</span>
              {apr.approved_hours != null && <span>Apr: {apr.approved_hours}h</span>}
              {p.actual_hours != null && <span>Act: {p.actual_hours}h</span>}
              {apr.penalty_hours > 0 && <span className="text-red-600">Penalty: {apr.penalty_hours}h</span>}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {fmtTime(p.out?.time)} → {fmtTime(p.in?.time)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TodosList({ rows, memberId, memberName, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [due, setDue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/todos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: title.trim(),
        description: desc.trim(),
        assignee_id: memberId,
        due_date: due || undefined,
      })
    });
    setSubmitting(false);
    if (result.ok) {
      setTitle(''); setDesc(''); setDue('');
      setAdding(false);
      onAdded && onAdded();
    } else {
      alert(result.error || 'Failed to create task');
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={() => setAdding(a => !a)}
        className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold"
        data-testid="team-todo-add-toggle"
      >
        <Plus size={14} /> Assign Task to {memberName}
      </button>

      {adding && (
        <div className="bg-gray-50 rounded-lg p-2 space-y-2 border" data-testid="team-todo-add-form">
          <input
            type="text" placeholder="Task title"
            value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm bg-white"
            data-testid="team-todo-title-input"
          />
          <textarea
            placeholder="Description (optional)"
            value={desc} onChange={(e) => setDesc(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded text-sm bg-white"
          />
          <div className="flex items-center gap-2">
            <input
              type="date" value={due} onChange={(e) => setDue(e.target.value)}
              className="flex-1 px-3 py-2 border rounded text-sm bg-white"
            />
            <button
              onClick={submit}
              disabled={!title.trim() || submitting}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm font-semibold"
              data-testid="team-todo-create-btn"
            >{submitting ? 'Saving…' : 'Assign'}</button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-center text-gray-500 py-4">No pending tasks.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map(t => (
            <li key={t.id} className="border rounded-lg p-2" data-testid={`team-todo-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-gray-900 text-sm flex-1">{t.title}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${t.created_by_role === 'admin' ? 'bg-purple-100 text-purple-700' : t.created_by !== t.assignee_id ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                  {t.created_by_role === 'admin' ? 'Admin' : (t.created_by !== t.assignee_id ? 'Senior' : 'Self')}
                </span>
              </div>
              {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
              <div className="text-[10px] text-gray-400 mt-0.5">
                {t.created_by_name && t.created_by !== t.assignee_id && <span>by {t.created_by_name}</span>}
                {t.due_date && <span> · due {t.due_date}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AttendanceList({ rows }) {
  if (rows.length === 0) return <p className="text-center text-gray-500 py-6">No attendance recorded for this date.</p>;
  return (
    <ul className="space-y-2">
      {rows.map(a => {
        const ci = a.check_in || {};
        const co = a.check_out || {};
        return (
          <li key={`${a.staff_id}-${a.date}`} className="border rounded-lg p-2" data-testid={`team-attendance-${a.date}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{a.date}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${a.status === 'present' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                {a.status || '—'}
              </span>
            </div>
            <div className="text-[11px] text-gray-600 mt-1 flex flex-wrap gap-x-3">
              <span>In: {fmtTime(ci.time)}</span>
              <span>Out: {fmtTime(co.time)}</span>
              <span>{a.total_hours ?? 0}h</span>
              <span className={ci.inside_geofence ? 'text-emerald-700' : 'text-red-700'}>
                {ci.inside_geofence ? 'inside' : 'outside'}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
